import type {
  MediationEntry,
  NetworkMetaRow,
  RefreshCadence,
  RefreshPolicy,
} from '@/api/networkMeta';

// 화면 라벨과 설명 — 컬럼 의미가 BigQuery description 에만 있으면 담당자가 화면에서
// 알 수 없으므로, 판정에 쓰이는 값들은 여기에 사람 말로 옮겨 둔다.

// 카탈로그 자동 채우기가 다루는 미디에이션. 서버도 같은 값을 응답에 실어 주지만,
// 목록을 받기 전에 그려야 하는 곳이 있어 기본값을 둔다.
export const ADMOB_MEDIATION = 'AdmobPro';

// 미디에이션 표시 순서. 백엔드 MEDIATION_ORDER 와 같은 목록이고, 값이 하나도 없는
// 미디에이션에도 탭을 열어 주기 위해 프런트에도 둔다(빈 탭이 없으면 새 이름을 넣을
// 자리가 생기지 않는다). 여기 없는 미디에이션은 행의 값에서 발견해 뒤에 붙인다.
export const MEDIATION_ORDER: string[] = [
  'applovinMAX',
  ADMOB_MEDIATION,
  'LevelPlay',
  'APS',
  'adjust',
  'appsflyer',
];

// 이 행이 실제로 보여 줄 미디에이션 탭 — 기본 순서 + 행에만 있는 새 값.
export function mediationTabs(entries: MediationEntry[]): string[] {
  const extra = entries
    .map((entry) => entry.mediation)
    .filter((name) => !MEDIATION_ORDER.includes(name));
  return [...MEDIATION_ORDER, ...Array.from(new Set(extra)).sort()];
}

// 정책은 세 값이다 — compare_max / always_refresh / null(자동 안 함).
// null 은 Record 의 키가 될 수 없어서 'none' 으로 좁혀 쓴다.
export type PolicyKey = RefreshPolicy | 'none';

export function policyKey(policy: RefreshPolicy | null): PolicyKey {
  return policy ?? 'none';
}

// 타팀이 읽는 화면이라 값 이름 대신 사람 말로 쓴다. 값 이름은 편집 드롭다운에서만
// 괄호로 함께 보인다 — 저장되는 값이 무엇인지도 알 수 있어야 하기 때문이다.
export const POLICY_LABEL: Record<PolicyKey, string> = {
  compare_max: '대조 판정',
  always_refresh: '전체 리프레시',
  none: '리프레시 안 함',
};

// 정책 → CSS 클래스 키. 템플릿 문자열로 조회하면 클래스명 오타를 타입 검사가 잡지 못한다.
export const POLICY_CLASS: Record<
  PolicyKey,
  'policyCompare' | 'policyAlwaysRefresh' | 'policyNone'
> = {
  compare_max: 'policyCompare',
  always_refresh: 'policyAlwaysRefresh',
  none: 'policyNone',
};

// 편집 드롭다운의 3택. 순서는 '많이 쓰는 것 → 안 함'.
export const POLICY_OPTIONS: { value: string; label: string }[] = [
  { value: 'compare_max', label: `${POLICY_LABEL.compare_max} (compare_max)` },
  { value: 'always_refresh', label: `${POLICY_LABEL.always_refresh} (always_refresh)` },
  // 빈 문자열이 곧 null 이다. 빈 칸으로 두면 '안 정했다'와 '안 한다'를 구분할 수 없어
  // 명시적 선택지로 만든다.
  { value: '', label: `${POLICY_LABEL.none} (NULL)` },
];

// 새 네트워크 폼 전용. 상세 화면은 값이 이미 있어서 빈 문자열을 NULL 로 쓰지만,
// 새로 만들 때는 '아직 안 고름'과 '리프레시 안 함'이 달라야 필수값이 뜻을 갖는다.
export const NEW_POLICY_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: '선택하세요' },
  { value: 'compare_max', label: `${POLICY_LABEL.compare_max} (compare_max)` },
  { value: 'always_refresh', label: `${POLICY_LABEL.always_refresh} (always_refresh)` },
  { value: 'none', label: `${POLICY_LABEL.none} (NULL)` },
];

// 위 선택값을 저장 값으로 옮긴다. 'none' 은 NULL, '' 는 아직 안 고른 상태다
// (제출 전에 필수값 검증이 막는다).
export function policyFromChoice(choice: string): RefreshPolicy | null {
  return choice === 'compare_max' || choice === 'always_refresh' ? choice : null;
}

export const CADENCE_LABEL: Record<RefreshCadence, string> = {
  monthly: '월간',
  quarterly: '분기',
};

export const CADENCES: RefreshCadence[] = ['monthly', 'quarterly'];

// 항목을 추가할 때 고를 수 있는 locale. 우리 수집 요청은 en-US 로 고정돼 있지만 과거
// 한국어 호출분이 표에 남아 있어, 그 라벨을 기록할 수단이 필요하다.
export const LOCALE_OPTIONS: { value: string; label: string }[] = [
  { value: 'en-US', label: 'en-US' },
  { value: 'ko', label: 'ko' },
];

// 표 열 설명 — 미디에이션 항목 표의 헤더에 붙는다. 열 이름만으로는 무엇을 넣는 칸인지
// 알 수 없는 둘에만 붙인다(api/dash 이름은 블록 캡션이 이미 설명한다).
export const ENTRY_COLUMN_HINT = {
  locale:
    '이 라벨이 어느 언어로 호출했을 때 오는 값인지입니다. 같은 네트워크가 언어별 항목으로 따로 존재할 수 있습니다.',
  custom_network:
    'CUSTOM_NETWORK_SDK 처럼 뭉뚱그려진 이름 안에서 실제 네트워크를 가르는 하위 구분자입니다.',
} as const;

// 컬럼별 설명 — 상세 화면 블록의 캡션. 화면에서 뺀 설명은 이 파일에 두지 않는다
// (남기면 어디서도 안 보이는 문구를 계속 관리하게 된다).
export const COLUMN_HINT = {
  gam_network_code:
    'GAM 리셀러의 광고단위 경로 코드입니다(MAX network_placement prefix 와 같은 값).',
  gam_placement_prefixes:
    '첫 번째 숫자가 Reseller Code입니다. 태그명은 식별에 사용하지 않으며 이전·신규 코드 이력을 함께 보존합니다.',
} as const;

export function placementRules(row: NetworkMetaRow): string[] {
  return row.gam_placement_prefixes?.length ? row.gam_placement_prefixes : row.gam_network_code;
}

export function validatePlacementRule(value: string): string | null {
  return value.length <= 512 && !/[\r\n\t]/.test(value) && /^\/[0-9]+(?:,[0-9]+)*\/[A-Za-z0-9_./ -]*$/.test(value)
    ? null : '/숫자코드/태그접두어 형식으로 입력해 주세요. 줄바꿈·탭·와일드카드는 사용할 수 없습니다.';
}

// 편집 중에만 사는 행 번호. 저장할 때 떼고 보내며 서버에도 BigQuery 에도 남지 않는다.
//
// 하는 일은 하나다 — 다시 그린 행이 직전의 그 행인지 알아보는 것. 그 자리에 값으로 만든
// 키를 쓰면 한 글자만 고쳐도 다른 행이 되어, React 가 입력칸을 새로 만들고 커서가 날아간다
// (api_name 을 고칠 때 한 글자마다 포커스가 빠지던 원인이다). diff 도 같은 이유로 한
// 항목의 수정을 '제거 + 추가' 두 줄로 냈다.
//
// 화면을 떠나면 번호가 사라지지만 잃을 게 없다 — 그 순간 저장하지 않은 편집도 함께
// 버려지기 때문이다(네트워크를 바꾸거나 새로 고치면 서버 값으로 돌아간다).
export interface RowEntry extends MediationEntry {
  rid: string;
}

// 새로 만든 행의 번호. b* 와 겹치지 않게 접두어를 나눈다.
let ridSeq = 0;
export function newRid(): string {
  ridSeq += 1;
  return `n${ridSeq}`;
}

// 서버에서 온 항목에 번호를 붙인다. 같은 응답이면 같은 번호가 나와야 한다 — before 와
// after 가 각각 이 함수를 거쳐 만들어지고, 둘을 번호로 짝지어 diff 를 낸다.
export function withRids(entries: MediationEntry[]): RowEntry[] {
  return entries.map((entry, index) => ({ ...entry, rid: `b${index}` }));
}

// 값 정리 — 행을 닫을 때와 저장할 때만 부른다. 타이핑 중에 부르면 끝의 공백이 그때그때
// 사라져 단어 사이를 띄울 수 없다.
export function trimEntry<T extends MediationEntry>(entry: T): T {
  const clean = (value: string | null) => {
    const trimmed = (value ?? '').trim();
    return trimmed === '' ? null : trimmed;
  };
  return {
    ...entry,
    api_name: clean(entry.api_name),
    dash_name: clean(entry.dash_name),
    custom_network: clean(entry.custom_network),
  };
}

// 저장 경계 — 행 번호를 떼고 앞뒤 공백을 턴다. 행을 열어 둔 채로 저장하면 닫을 때
// 하는 정리를 못 거치므로 여기서 한 번 더 한다.
export function toPayload(entries: RowEntry[]): MediationEntry[] {
  return entries.map(({ rid: _rid, ...entry }) => trimEntry(entry));
}

// 전 행에서 기존 BI 그룹 목록을 모은다 — 여기 없는 값을 넣는 것이 곧 그룹 생성이다.
// 상세 화면과 새 네트워크 폼이 같은 목록을 써야 한쪽만 그룹을 못 보는 일이 없다.
export function collectBiGroups(rows: Pick<NetworkMetaRow, 'bi_network_name'>[]): string[] {
  return [...new Set(rows.flatMap((row) => row.bi_network_name))].filter(Boolean);
}

export function entriesOf(row: NetworkMetaRow, mediation: string): MediationEntry[] {
  return row.mediation_info.filter((entry) => entry.mediation === mediation);
}

export function formatThreshold(row: Pick<NetworkMetaRow, 'threshold_pct'>): string {
  if (row.threshold_pct === null) return '—';
  return `${row.threshold_pct}%p`;
}

// 목록 검색 — sc_naming 뿐 아니라 모든 미디에이션 이름과 AdMob id 까지 훑는다.
// '이 리포트 이름이 어느 네트워크에 붙어 있는지' 찾는 게 실제 용도다.
export function matchesQuery(row: NetworkMetaRow, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;
  const haystack = [
    row.sc_naming,
    row.refresh_note ?? '',
    ...row.bi_network_name,
    ...row.gam_network_code,
    ...(row.gam_placement_prefixes ?? []),
    POLICY_LABEL[policyKey(row.refresh_policy_monthly)],
    POLICY_LABEL[policyKey(row.refresh_policy_quarterly)],
    // 이름이 두 벌이라 둘 다 훑는다 — 담당자는 대시보드 이름으로 찾는 경우가 많다.
    ...row.mediation_info.flatMap((entry) => [
      entry.mediation,
      entry.api_name ?? '',
      entry.dash_name ?? '',
      entry.custom_network ?? '',
    ]),
  ];
  return haystack.some((value) => value.toLowerCase().includes(needle));
}
